/**
 * Owner: Masa Bayu
 * File: coverage-batches.ts
 * Date:Time: 2026-09-20 01:05
 * Description: CERT-GLOBAL CLOSED — Batch 7 Supported · Batch 8 OPEN.
 */
import { getCoverageFamily } from './coverage-families';
function batch(batchId, batchIndex, familyIds, rationale, executionStatus) {
    return Object.freeze({
        batchId,
        batchIndex,
        familyIds: Object.freeze([...familyIds]),
        rationale,
        batchOpenImpliesSupported: false,
        executionStatus,
    });
}
export const QX_COVERAGE_BATCHES = Object.freeze([
    batch('batch-0-latin-core', 0, ['latin-core'], 'CERT-01 proof path', 'completed'),
    batch('batch-1-rtl', 1, ['arabic', 'hebrew'], 'RTL / bidi (ALIGN I-02 · I-06 · I-07)', 'completed'),
    batch('batch-2-cyrillic-greek', 2, ['cyrillic', 'greek'], 'Major European non-Latin', 'completed'),
    batch('batch-3-cjk', 3, ['chinese', 'japanese', 'korean'], 'CJK shaping / typography', 'completed'),
    batch('batch-4-sea', 4, ['thai', 'lao', 'khmer', 'myanmar'], 'SEA complex / no-space scripts', 'completed'),
    batch('batch-5-indic', 5, [
        'devanagari',
        'bengali',
        'gurmukhi',
        'gujarati',
        'odia',
        'tamil',
        'telugu',
        'kannada',
        'malayalam',
        'sinhala',
    ], 'Indic abugida cluster', 'completed'),
    batch('batch-6-latin-extended', 6, ['latin-extended'], 'Extended Latn diacritics after core', 'completed'),
    batch('batch-7-regional', 7, ['armenian', 'georgian', 'ethiopic', 'tibetan'], 'Regional syllabaries / scripts', 'completed'),
    batch('batch-8-specialist', 8, [
        'canadian-aboriginal',
        'southeast-asian-special',
        'other-specialist',
    ], 'Specialist · lowest auto-priority', 'open'),
]);
export function createCoverageBatchMatrix() {
    return QX_COVERAGE_BATCHES;
}
export function listFamilyIdsInBatch(batchIndex) {
    const row = QX_COVERAGE_BATCHES.find((b) => b.batchIndex === batchIndex);
    if (!row) {
        throw new Error(`unknown coverage batch index: ${batchIndex}`);
    }
    return row.familyIds;
}
export function getCoverageBatch(batchIndex) {
    const row = QX_COVERAGE_BATCHES.find((b) => b.batchIndex === batchIndex);
    if (!row) {
        throw new Error(`unknown coverage batch index: ${batchIndex}`);
    }
    return row;
}
export function listOpenCoverageBatchIndexes() {
    return Object.freeze(QX_COVERAGE_BATCHES.filter((b) => b.executionStatus === 'open').map((b) => b.batchIndex));
}
/** All 30 families appear exactly once across batches. */
export function assertCoverageBatchesCoverAllFamilies(familyIds) {
    const seen = new Set();
    for (const row of QX_COVERAGE_BATCHES) {
        for (const id of row.familyIds) {
            if (seen.has(id)) {
                return false;
            }
            seen.add(id);
        }
    }
    if (seen.size !== familyIds.length) {
        return false;
    }
    return familyIds.every((id) => seen.has(id));
}
export function batchDoesNotImplySupported(batchIndex) {
    const row = QX_COVERAGE_BATCHES.find((b) => b.batchIndex === batchIndex);
    if (!row) {
        return false;
    }
    if (row.batchOpenImpliesSupported) {
        return false;
    }
    // Open batches must not contain Supported families (Accept only after READY).
    if (row.executionStatus === 'open') {
        for (const familyId of row.familyIds) {
            const family = getCoverageFamily(familyId);
            if (!family || family.lifecycle === 'supported') {
                return false;
            }
        }
    }
    return true;
}
/**
 * While Batch 1 is open: families must not be Supported yet.
 * After Owner Accept (completed): arabic · hebrew must be Supported.
 */
export function assertBatch1OpenNotSupported() {
    const row = getCoverageBatch(1);
    if (row.executionStatus === 'completed') {
        return assertBatch1CompletedSupported();
    }
    if (row.executionStatus !== 'open') {
        return false;
    }
    const expected = new Set(['arabic', 'hebrew']);
    if (row.familyIds.length !== expected.size ||
        !row.familyIds.every((id) => expected.has(id))) {
        return false;
    }
    const allowed = new Set([
        'registered',
        'fixtures',
        'evidence_path',
        'test',
        'ready',
    ]);
    for (const familyId of row.familyIds) {
        const family = getCoverageFamily(familyId);
        if (!family || !allowed.has(family.lifecycle)) {
            return false;
        }
    }
    return true;
}
/** Batch 1 completed via Owner Accept — arabic · hebrew Supported. */
export function assertBatch1CompletedSupported() {
    const row = getCoverageBatch(1);
    if (row.executionStatus !== 'completed') {
        return false;
    }
    for (const familyId of ['arabic', 'hebrew']) {
        const family = getCoverageFamily(familyId);
        if (!family || family.lifecycle !== 'supported' || !family.ownerAcceptedAt) {
            return false;
        }
    }
    return true;
}
/**
 * While Batch 2 is open: cyrillic · greek must not be Supported.
 * Allowed ladder stages through READY.
 */
export function assertBatch2OpenNotSupported() {
    const row = getCoverageBatch(2);
    if (row.executionStatus === 'completed') {
        return assertBatch2CompletedSupported();
    }
    if (row.executionStatus !== 'open') {
        return false;
    }
    const expected = new Set(['cyrillic', 'greek']);
    if (row.familyIds.length !== expected.size ||
        !row.familyIds.every((id) => expected.has(id))) {
        return false;
    }
    const allowed = new Set([
        'registered',
        'fixtures',
        'evidence_path',
        'test',
        'ready',
    ]);
    for (const familyId of row.familyIds) {
        const family = getCoverageFamily(familyId);
        if (!family || !allowed.has(family.lifecycle)) {
            return false;
        }
    }
    return true;
}
/** Batch 2 completed via Owner Accept — cyrillic · greek Supported. */
export function assertBatch2CompletedSupported() {
    const row = getCoverageBatch(2);
    if (row.executionStatus !== 'completed') {
        return false;
    }
    for (const familyId of ['cyrillic', 'greek']) {
        const family = getCoverageFamily(familyId);
        if (!family || family.lifecycle !== 'supported' || !family.ownerAcceptedAt) {
            return false;
        }
    }
    return true;
}
/**
 * While Batch 3 is open: chinese · japanese · korean must not be Supported.
 * After Owner Accept (completed): families must be Supported.
 */
export function assertBatch3OpenNotSupported() {
    const row = getCoverageBatch(3);
    if (row.executionStatus === 'completed') {
        return assertBatch3CompletedSupported();
    }
    if (row.executionStatus !== 'open') {
        return false;
    }
    const expected = new Set(['chinese', 'japanese', 'korean']);
    if (row.familyIds.length !== expected.size ||
        !row.familyIds.every((id) => expected.has(id))) {
        return false;
    }
    const allowed = new Set([
        'registered',
        'fixtures',
        'evidence_path',
        'test',
        'ready',
    ]);
    for (const familyId of row.familyIds) {
        const family = getCoverageFamily(familyId);
        if (!family || !allowed.has(family.lifecycle)) {
            return false;
        }
    }
    return true;
}
/** Batch 3 completed via Owner Accept — chinese · japanese · korean Supported. */
export function assertBatch3CompletedSupported() {
    const row = getCoverageBatch(3);
    if (row.executionStatus !== 'completed') {
        return false;
    }
    for (const familyId of ['chinese', 'japanese', 'korean']) {
        const family = getCoverageFamily(familyId);
        if (!family || family.lifecycle !== 'supported' || !family.ownerAcceptedAt) {
            return false;
        }
    }
    return true;
}
/**
 * While Batch 4 is open: thai · lao · khmer · myanmar must not be Supported.
 * After Owner Accept (completed): families must be Supported.
 */
export function assertBatch4OpenNotSupported() {
    const row = getCoverageBatch(4);
    if (row.executionStatus === 'completed') {
        return assertBatch4CompletedSupported();
    }
    if (row.executionStatus !== 'open') {
        return false;
    }
    const expected = new Set(['thai', 'lao', 'khmer', 'myanmar']);
    if (row.familyIds.length !== expected.size ||
        !row.familyIds.every((id) => expected.has(id))) {
        return false;
    }
    const allowed = new Set([
        'registered',
        'fixtures',
        'evidence_path',
        'test',
        'ready',
    ]);
    for (const familyId of row.familyIds) {
        const family = getCoverageFamily(familyId);
        if (!family || !allowed.has(family.lifecycle)) {
            return false;
        }
    }
    return true;
}
/** Batch 4 completed via Owner Accept — thai · lao · khmer · myanmar Supported. */
export function assertBatch4CompletedSupported() {
    const row = getCoverageBatch(4);
    if (row.executionStatus !== 'completed') {
        return false;
    }
    for (const familyId of ['thai', 'lao', 'khmer', 'myanmar']) {
        const family = getCoverageFamily(familyId);
        if (!family || family.lifecycle !== 'supported' || !family.ownerAcceptedAt) {
            return false;
        }
    }
    return true;
}
//# sourceMappingURL=coverage-batches.js.map